import { DeleteConfirmationModal } from "@mirage/mosaics/ComposeAssistant/components/settings/DeleteConfirmationModal";
import { useConversationsList } from "@mirage/service-conversation/hooks/useConversationsList";
import i18n from "@mirage/translations";
import { useAtom } from "jotai";
import { memo, useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ConversationListPopup } from "../components/sessions/ConversationListPopup";
import { ConversationListRows } from "../components/sessions/ConversationListRows";

import {
  urlForConversationWithServerID,
  useConversationIdFromSearchParams,
} from "@mirage/mosaics/Chat/utils/navigation";
import { useComposeSessionsContext } from "@mirage/mosaics/ComposeAssistant/data/ComposeSessionsContext";
import { conversationPageLoadingAtom } from "@mirage/mosaics/ConversationAssistant/hooks/useConversationPageLoading";
import { deleteConversations } from "@mirage/service-conversation";
import type { ConversationMetadata } from "@mirage/service-conversation/types";
import type {
  ConversationRouterState,
  MessageHistory,
} from "@mirage/service-conversation/utils/buildConversationRouterState";
import {
  buildConversationRouterState,
  CtaSource,
} from "@mirage/service-conversation/utils/buildConversationRouterState";
import type { ComposeAssistantConversationMessage } from "@mirage/shared/compose/assist-message";
import {
  getFirstMarkdownArtifact,
  type ComposeSession,
} from "@mirage/shared/compose/compose-session";
import { useIsSmallSizeForSidebar } from "@mirage/shared/responsive/mobile";
import { ConversationListModal } from "../components/sessions/ConversationListModal";

const DISPLAY_CONFIG = {
  ROWS: {
    MAX_ITEMS: 5,
  },
  POPUP: {
    MAX_ITEMS: 7,
  },
} as const;

// Transform legacy session message to conversation message format
const transformMessageToHistory = (
  message: ComposeAssistantConversationMessage,
): MessageHistory => ({
  role:
    message.role === "user" || message.role === "assistant"
      ? message.role
      : "assistant", // Default to assistant for system or other roles
  content: message.type === "message" ? message.text : "",
  attachments:
    message.type === "message" && message.referencingSources
      ? message.referencingSources
      : [],
});

interface ConversationListProps {
  showListPreview: boolean;
  onOpenTemplates: () => void;
  handleStartNewSessionWithState: (
    routerState?: ConversationRouterState,
  ) => void;
}

export const ConversationList = memo(
  ({
    showListPreview,
    onOpenTemplates,
    handleStartNewSessionWithState,
  }: ConversationListProps) => {
    const navigate = useNavigate();
    const isSmallSize = useIsSmallSizeForSidebar();
    const [showModal, setShowModal] = useState(false);
    const [conversationToDelete, setConversationToDelete] =
      useState<ConversationMetadata | null>(null);
    const [searchParams] = useSearchParams();
    const currentConversationIdLookup =
      useConversationIdFromSearchParams(searchParams);
    const conversationListState = useConversationsList();
    const [, setIsConversationPageLoading] = useAtom(
      conversationPageLoadingAtom,
    );
    const { sessions: legacySessions = [] } = useComposeSessionsContext();

    // Sort legacy sessions in reverse chronological order
    const sortedLegacySessions = useMemo(
      () =>
        [...legacySessions].sort(
          (a, b) => (b.lastUpdated ?? 0) - (a.lastUpdated ?? 0),
        ),
      [legacySessions],
    );

    const handleLegacySessionClick = useCallback(
      (session: ComposeSession, onClose?: () => void) => {
        const messageHistory = session.messagesHistory.map(
          transformMessageToHistory,
        );
        const markdownArtifact = getFirstMarkdownArtifact(session.artifacts);
        const conversationRouterState = buildConversationRouterState(
          CtaSource.LegacySession,
          {
            messageHistory,
            markdownContent: markdownArtifact?.markdownContent,
          },
        );
        handleStartNewSessionWithState(conversationRouterState);
        onClose?.();
      },
      [handleStartNewSessionWithState],
    );

    const handleClickConversation = useCallback(
      (conversation: ConversationMetadata) => {
        setIsConversationPageLoading(true);
        navigate(
          urlForConversationWithServerID(conversation.serverConversationId),
          {
            replace: true,
          },
        );
      },
      [navigate, setIsConversationPageLoading],
    );

    const handleDeleteConversation = useCallback(
      (conversation: ConversationMetadata) => {
        setConversationToDelete(conversation);
      },
      [],
    );

    const handleConfirmDelete = useCallback(() => {
      if (conversationToDelete) {
        deleteConversations([conversationToDelete.serverConversationId]);
        setConversationToDelete(null);
      }
    }, [conversationToDelete]);

    const handleCancelDelete = useCallback(() => {
      setConversationToDelete(null);
    }, []);

    const currentConversationId =
      currentConversationIdLookup.state === "loaded"
        ? currentConversationIdLookup.conversationId
        : undefined;

    const conversationList =
      (conversationListState.type === "loaded" &&
        conversationListState.conversationMetadatas.length > 0 &&
        conversationListState.conversationMetadatas.map((m) => ({
          ...m,
          title: cleanTitle(m.title),
          preview: cleanTitle(m.preview),
        }))) ||
      [];

    const getConversationsToDisplay = (maxItems: number) => {
      const result: (ConversationMetadata | ComposeSession)[] =
        conversationList.slice(0, maxItems);
      const remainingSlots = maxItems - result.length;
      if (remainingSlots > 0) {
        result.push(...sortedLegacySessions.slice(0, remainingSlots));
      }
      return result;
    };

    const conversationsToDisplayInRows = useMemo(
      () => getConversationsToDisplay(DISPLAY_CONFIG.ROWS.MAX_ITEMS),
      [conversationList, sortedLegacySessions],
    );

    const conversationsToDisplayInPopup = useMemo(
      () => getConversationsToDisplay(DISPLAY_CONFIG.POPUP.MAX_ITEMS),
      [conversationList, sortedLegacySessions],
    );

    return (
      <>
        <ConversationListPopup
          conversationsToDisplay={conversationsToDisplayInPopup}
          onClickConversation={handleClickConversation}
          setShowModal={setShowModal}
          disabled={showListPreview && !isSmallSize}
          onDeleteConversation={handleDeleteConversation}
          onOpenTemplates={onOpenTemplates}
          handleLegacySessionClick={handleLegacySessionClick}
        />
        {showListPreview && !isSmallSize && (
          <ConversationListRows
            conversationListState={conversationListState}
            conversationsToDisplay={conversationsToDisplayInRows}
            currentConversationServerId={currentConversationId}
            onClickConversation={handleClickConversation}
            onDeleteConversation={handleDeleteConversation}
            setShowModal={setShowModal}
            handleLegacySessionClick={handleLegacySessionClick}
          />
        )}
        <ConversationListModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          conversations={conversationList}
          onClickConversation={handleClickConversation}
          onDeleteConversation={handleDeleteConversation}
          sortedLegacySessions={sortedLegacySessions}
          handleLegacySessionClick={handleLegacySessionClick}
        />
        {conversationToDelete && (
          <DeleteConfirmationModal
            title={i18n.t("compose_delete_session_confirm_title")}
            description={i18n.t("compose_delete_session_confirm_description")}
            onSubmit={handleConfirmDelete}
            onCancel={handleCancelDelete}
            isOpen={!!conversationToDelete}
          />
        )}
      </>
    );
  },
);

ConversationList.displayName = "ConversationList";

function cleanTitle(originalTitle: string) {
  return originalTitle.replace(/^#+\s+/, "").trim();
}
